import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/toast';
import {
  useCancelDomainOrder,
  useCreateDomainOrder,
  useDomainOrders,
  useDomainOrdersConfig,
  useDomainSearch,
  type DomainCandidate,
  type DomainRegistrantInput,
  type DomainSearchParsed,
} from '@/data/domain-orders';
import {
  useCheckPortalDomain,
  useClaimPortalDomain,
  usePortalDomains,
} from '@/data/portal-domains';
import { recordSetupEvent } from '@/data/setup-wizard';
import { cn } from '@/lib/cn';

/**
 * Setup wizard — the address step's domain pieces (purchase-in-wizard unit):
 * the 3-way choice cards, the ② buy sub-step (search → registrant → Stripe →
 * poll), and the ③ BYO sub-step (claim → records → verify-later). All reuse
 * the live domain-orders / portal-domains rails; nothing here is Settings-
 * specific. Guard rails: a back-out/failure never strands signup (explicit
 * "use my free subdomain" fallback that cancels the order and frees the name).
 */

export type DomainChoice = 'free' | 'buy' | 'byo';

function formatPrice(pence: number): string {
  return pence % 100 === 0 ? `£${pence / 100}` : `£${(pence / 100).toFixed(2)}`;
}

const TERMS_URL = 'https://breakernetwork.app/terms';
// Registrar-side agreements (Lee-verified live 2026-08-16).
const DOMAIN_REGISTRATION_TERMS_URL =
  'https://www.spaceship.com/legal/domain-registration-agreement/';
const WHOIS_PRIVACY_TERMS_URL =
  'https://www.spaceship.com/legal/whois-privacy-service-agreement/';

/** Registrar-style search input attrs: mobile keyboards must not capitalise
 *  or "correct" a domain (the server normalises again regardless). */
export const domainQueryInputProps = {
  autoCapitalize: 'none',
  autoCorrect: 'off',
  spellCheck: false,
  inputMode: 'url',
} as const;

/** The menu before you type: the TLDs we actually sell, with prices. */
export function TldPriceChips({ fleetId }: { fleetId: string | null }) {
  const config = useDomainOrdersConfig(fleetId);
  const tlds = config.data?.tlds ?? [];
  // 0085 — false for every fleet that never redeemed a founding code, so
  // this whole component renders exactly as it always has for them.
  const compIncluded = config.data?.compEntitlement.available ?? false;
  if (!tlds.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" aria-label="Available domain endings">
      {tlds.map((t) => (
        <span
          key={t.tld}
          className="inline-flex items-baseline gap-1 rounded-full border border-breaker-line bg-breaker-ground-2 px-2 py-0.5 text-xs"
        >
          <span className="ref text-breaker-ink">.{t.tld}</span>
          {compIncluded ? (
            <span className="text-breaker-mute line-through">{formatPrice(t.retailPence)}</span>
          ) : (
            <span className="text-breaker-mute">{formatPrice(t.retailPence)}/year</span>
          )}
        </span>
      ))}
      {compIncluded ? (
        <span className="inline-flex items-baseline rounded-full border border-breaker-identity/40 bg-breaker-identity/10 px-2 py-0.5 text-xs text-breaker-identity">
          Included with your founding code
        </span>
      ) : null}
    </div>
  );
}

/**
 * Search results, registrar-style: exact typed domain first (server order),
 * selection reads on the ROW, price gets its own line at 390px, and taken /
 * premium rows are clearly secondary. Shared by the wizard and Settings.
 */
export function DomainResultsList({
  results,
  parsed,
  chosen,
  onChoose,
  compIncluded = false,
}: {
  results: DomainCandidate[];
  parsed: DomainSearchParsed | null;
  chosen: DomainCandidate | null;
  onChoose: (c: DomainCandidate) => void;
  /** 0085 — the fleet's founding comp code covers this registration. Default
   *  false keeps every existing caller and every non-comp fleet identical. */
  compIncluded?: boolean;
}) {
  return (
    <div className="space-y-2">
      {parsed?.unsupportedTld ? (
        <p className="text-xs text-breaker-mute rounded-md bg-breaker-ground-2 px-3 py-2">
          We don&rsquo;t offer <span className="ref">.{parsed.unsupportedTld}</span> —
          here&rsquo;s <span className="ref">{parsed.label}</span> on the endings we do:
        </p>
      ) : null}
      <ul className="space-y-2">
        {results.map((c) => {
          const isChosen = chosen?.domain === c.domain;
          if (!c.available) {
            return (
              <li
                key={c.domain}
                className="rounded-md border border-breaker-line bg-breaker-ground-2 px-3 py-2"
              >
                <span className="text-xs text-breaker-mute">
                  <span className="ref">{c.domain}</span>{' '}
                  {c.premium
                    ? 'is a premium name — Breaker only offers flat-price domains.'
                    : 'is taken.'}
                </span>
              </li>
            );
          }
          return (
            <li
              key={c.domain}
              className={cn(
                'rounded-md border px-3 py-2 transition-colors',
                isChosen ? 'border-breaker-identity bg-breaker-identity/10' : 'border-breaker-line',
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm min-w-0">
                  <span className="ref break-words">{c.domain}</span>
                  <span className="block text-xs text-breaker-mute">
                    {c.retailPence === null ? (
                      'available'
                    ) : compIncluded ? (
                      <>
                        <span className="line-through">{formatPrice(c.retailPence)}/year</span>{' '}
                        <span className="text-breaker-identity">
                          included with your founding code
                        </span>
                      </>
                    ) : (
                      `${formatPrice(c.retailPence)}/year`
                    )}
                  </span>
                </span>
                <Button
                  size="sm"
                  className="shrink-0"
                  variant={isChosen ? 'secondary' : 'primary'}
                  onClick={() => onChoose(c)}
                >
                  {isChosen ? '✓ Chosen' : 'Choose'}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The one REQUIRED tick that gates Pay: T&Cs + registration terms, with the
 * explicit registrant consent folded in, in plain words — a registrar
 * requirement that must stay inside the required tick.
 */
export function RegistrationTermsConsent({
  domain,
  checked,
  onChange,
}: {
  domain: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span className="text-sm text-breaker-ink">
        I agree to the{' '}
        <a
          href={TERMS_URL}
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-breaker-identity"
        >
          terms and conditions
        </a>{' '}
        and the{' '}
        <a
          href={DOMAIN_REGISTRATION_TERMS_URL}
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-breaker-identity"
        >
          domain registration terms
        </a>
        , and confirm these details are mine, to register{' '}
        <span className="ref">{domain}</span> in my name.
      </span>
    </label>
  );
}

/**
 * Optional WHOIS-privacy control, honest per registry:
 * - .uk / .co.uk: Nominet has NO registrar privacy proxy and lists domain
 *   owners in its public register, so we neither offer nor promise WHOIS
 *   privacy here — only an honest note (individuals can ask Nominet to
 *   withhold their address directly). The stored preference has no effect;
 *   fulfil registers 'public' on .uk regardless.
 * - .com and other gTLDs: a real WHOIS-privacy proxy, offered as an
 *   opt-out toggle defaulting ON.
 */
export function WhoisPrivacyControl({
  domain,
  value,
  onChange,
}: {
  domain: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  const isUk = /\.uk$/i.test(domain.trim());
  if (isUk) {
    return (
      <p className="text-xs text-breaker-mute rounded-md bg-breaker-ground-2 px-3 py-2">
        Heads up: Nominet lists <span className="ref">.uk</span> domain owners in its public
        register, so we can&rsquo;t hide your details in WHOIS on this one. If you&rsquo;re an
        individual, you can ask Nominet to withhold your address after registration.
      </p>
    );
  }
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span className="text-sm text-breaker-ink">
        Keep my details out of the public WHOIS directory{' '}
        <span className="text-breaker-mute">
          · recommended ·{' '}
          <a
            href={WHOIS_PRIVACY_TERMS_URL}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-breaker-identity"
            onClick={(e) => e.stopPropagation()}
          >
            what this covers
          </a>
        </span>
      </span>
    </label>
  );
}

export function DomainChoiceCards({
  choice,
  onChoose,
  buyEnabled,
  price,
  exampleDomain,
  section = 'all',
}: {
  choice: DomainChoice;
  onChoose: (c: DomainChoice) => void;
  buyEnabled: boolean;
  price: string | null;
  exampleDomain: string;
  /** Which group of choices to render — the step-3 clarity pass splits the
   *  free address ('free', TOP) from the buy/connect upgrade pair ('upgrade',
   *  BOTTOM). 'all' keeps the original single stack. */
  section?: 'free' | 'upgrade' | 'all';
}) {
  const showFree = section === 'free' || section === 'all';
  const showUpgrade = section === 'upgrade' || section === 'all';
  const card = (c: DomainChoice, title: React.ReactNode, body: React.ReactNode) => (
    <button
      type="button"
      onClick={() => onChoose(c)}
      className={cn(
        'w-full text-left rounded-md border p-3 transition-colors',
        choice === c
          ? 'border-breaker-identity bg-breaker-identity/10'
          : 'border-breaker-line hover:bg-breaker-ground-2',
      )}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className={cn(
            'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
            choice === c ? 'border-breaker-identity bg-breaker-identity' : 'border-breaker-line-2',
          )}
        >
          {choice === c ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-breaker-ink">{title}</p>
          <p className="text-xs text-breaker-mute mt-0.5">{body}</p>
        </div>
      </div>
    </button>
  );

  return (
    <div className="space-y-2">
      {showFree
        ? card(
            'free',
            <>Use this free address <span className="text-breaker-mute font-normal">· recommended</span></>,
            // The host itself is NOT repeated here: it is already shown in
            // full directly above this card ("Customers will visit …"), and
            // the pre-selected door's job is to answer "what does picking
            // this cost me later?", not to restate the address.
            <>Live instantly, free forever — add your own domain later in Settings.</>,
          )
        : null}
      {showUpgrade && buyEnabled
        ? card(
            'buy',
            <>Buy your own domain {price ? <span className="text-breaker-mute font-normal">· {price}/year</span> : null}</>,
            <>
              Get <span className="ref">{exampleDomain}</span> — we register it in your name and set
              everything up.
            </>,
          )
        : null}
      {showUpgrade
        ? card(
            'byo',
            'Connect a domain I own',
            <>Point an existing domain at your booking page with two DNS records.</>,
          )
        : null}
    </div>
  );
}

// ---- ② buy sub-step ---------------------------------------------------------

type BuySub = 'search' | 'polling' | 'fallback';

export function WizardDomainBuyStep({
  fleetId,
  seed,
  slugHost,
  initialSub,
  onContinueFree,
  onDomainDone,
}: {
  fleetId: string;
  /** Brand seed for the search box. */
  seed: string;
  /** The fleet's free subdomain, shown on the fallback action. */
  slugHost: string;
  /** Resume entry point: 'polling' when returning from Stripe success. */
  initialSub: BuySub;
  /** The free-subdomain fallback (cancels any open order first). */
  onContinueFree: () => void;
  /** Domain registered — advance the wizard. */
  onDomainDone: () => void;
}) {
  const toast = useToast();
  const domainConfig = useDomainOrdersConfig(fleetId);
  // 0085 — by this sub-step the fleet EXISTS and any comp code has been
  // redeemed, so the entitlement is knowable here. (The step-3 choice cards
  // render before the fleet is created, so they cannot know it and are
  // deliberately left alone — at that moment the list price is the truth.)
  const compIncluded = domainConfig.data?.compEntitlement.available ?? false;
  const search = useDomainSearch(fleetId);
  const createOrder = useCreateDomainOrder(fleetId);
  const cancelOrder = useCancelDomainOrder(fleetId);
  const orders = useDomainOrders(fleetId);
  const [sub, setSub] = useState<BuySub>(initialSub);
  const [query, setQuery] = useState(seed);
  const [results, setResults] = useState<DomainCandidate[] | null>(null);
  const [parsed, setParsed] = useState<DomainSearchParsed | null>(null);
  const [chosen, setChosen] = useState<DomainCandidate | null>(null);
  const [reg, setReg] = useState<DomainRegistrantInput>({
    firstName: '',
    lastName: '',
    email: '',
    address1: '',
    city: '',
    postalCode: '',
    phone: '',
  });
  const [organization, setOrganization] = useState('');
  // #3: registration consent (required, gates Pay) is split from the optional
  // WHOIS-privacy preference (default on).
  const [registrationConsent, setRegistrationConsent] = useState(false);
  const [whoisPrivacy, setWhoisPrivacy] = useState(true);
  const purchaseRecorded = useRef(false);

  useEffect(() => {
    recordSetupEvent(fleetId, 'wizard_domain_started');
  }, [fleetId]);

  // The latest register order — drives the polling panel and completion.
  const latest = (orders.data ?? []).find((o) => o.kind === 'register') ?? null;

  // #2: the confirmation beat is a STOP — record the purchase, but NEVER
  // auto-advance (sandbox settles instantly and used to flash past). The
  // panel shows a live status + an explicit "Continue to setup" button.
  useEffect(() => {
    if (sub !== 'polling' || !latest) return;
    if (latest.status === 'succeeded') {
      if (!purchaseRecorded.current) {
        purchaseRecorded.current = true;
        recordSetupEvent(fleetId, 'wizard_domain_purchased');
      }
    } else if (latest.status === 'failed' || latest.status === 'refunded') {
      setSub('fallback');
    }
  }, [sub, latest, fleetId]);

  function runSearch() {
    search
      .mutateAsync(query)
      .then((r) => {
        setResults(r.candidates);
        setParsed(r.parsed);
        setChosen(null);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Search failed'));
  }

  function pay() {
    if (!chosen) return;
    createOrder
      .mutateAsync({
        domain: chosen.domain,
        registrant: {
          ...reg,
          ...(organization.trim() ? { organization: organization.trim() } : {}),
        },
        registrationConsent,
        whoisPrivacy,
        returnTo: 'wizard',
      })
      .then((r) => {
        // 0085: a comp order returns no checkout URL — there is no card step.
        // The order is already fulfilling, so fall through to the polling
        // panel exactly where Stripe's success_url would have landed us.
        if (r.checkoutUrl) {
          window.location.assign(r.checkoutUrl);
        }
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Could not start payment'));
  }

  function fallbackToFree() {
    // Free the searched name if an order is still open, then continue free.
    if (latest && latest.status === 'awaiting_payment') {
      cancelOrder.mutateAsync(latest.id).catch(() => undefined);
    } else {
      recordSetupEvent(fleetId, 'wizard_domain_fell_back');
    }
    onContinueFree();
  }

  // A proper secondary action, not small print: the buyer must always see
  // the no-cost way out, with the actual address on it.
  const FreeFallbackLink = (
    <div className="space-y-1">
      <Button variant="secondary" className="w-full" onClick={fallbackToFree}>
        <span className="min-w-0 truncate">
          or continue with <span className="ref">{slugHost}</span> — free forever
        </span>
      </Button>
      <p className="text-center text-2xs text-breaker-mute">
        You can buy a domain any time from Settings.
      </p>
    </div>
  );

  if (sub === 'polling') {
    // failed/refunded is handed to the fallback panel by the effect above.
    if (latest && (latest.status === 'failed' || latest.status === 'refunded')) return null;
    const boughtName = latest?.domain;
    const done = latest?.status === 'succeeded';
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-breaker-green/30 bg-breaker-green/10 p-4 space-y-2">
          <p className="text-sm font-semibold text-breaker-ink">Thanks — payment received.</p>
          {done ? (
            <>
              <p className="text-sm text-breaker-ink">
                ✓ {boughtName ? <span className="ref">{boughtName}</span> : 'Your domain'} is
                registered and set up.
              </p>
              <p className="text-sm text-breaker-mute">
                Your booking page, admin address and email records are ready. Continue whenever
                you like.
              </p>
            </>
          ) : (
            <>
              {/* One text span beside the spinner so the sentence wraps as
                  flowing prose at 390px, not as stacked flex items. */}
              <p className="text-sm text-breaker-ink flex items-start gap-2">
                <Spinner size="sm" />
                <span>
                  Registering{' '}
                  {boughtName ? <span className="ref">{boughtName}</span> : 'your domain'} with
                  the registry…
                </span>
              </p>
              <p className="text-sm text-breaker-mute">
                Usually a minute or two. It carries on in the background — you can continue to
                setup now and we&rsquo;ll email you the moment it&rsquo;s live.
              </p>
            </>
          )}
        </div>
        {/* The deliberate beat: never auto-advance, even when fulfil is
            instant. The user chooses to move on. */}
        <Button className="w-full" size="lg" onClick={onDomainDone}>
          Continue to setup
        </Button>
      </div>
    );
  }

  if (sub === 'fallback') {
    const refunded = latest?.status === 'refunded';
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-breaker-line bg-breaker-ground-2 p-3">
          <p className="text-sm text-breaker-ink">
            We couldn&rsquo;t complete the domain purchase
            {refunded ? ' — you haven’t been charged, it’s refunded in full' : ''}. No
            problem — your free subdomain is already live and you can buy a domain any time from
            Settings.
          </p>
        </div>
        <Button className="w-full" size="lg" onClick={fallbackToFree}>
          Continue with your free subdomain
        </Button>
      </div>
    );
  }

  // sub === 'search'. Pay gates ONLY on registration consent — the WHOIS
  // preference is optional and must never block an honest buyer.
  const formReady =
    registrationConsent &&
    reg.firstName.trim() &&
    reg.lastName.trim() &&
    reg.email.trim() &&
    reg.address1.trim() &&
    reg.city.trim() &&
    reg.postalCode.trim() &&
    reg.phone.trim();

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Field label="Search for a name" htmlFor="wizDomainSeed">
              <Input
                id="wizDomainSeed"
                value={query}
                onChange={(e) => setQuery(e.target.value.toLowerCase())}
                placeholder="harbourcouriers or harbourcouriers.co.uk"
                {...domainQueryInputProps}
              />
            </Field>
          </div>
          <Button size="sm" onClick={runSearch} loading={search.isPending} disabled={query.trim().length < 2}>
            Search
          </Button>
        </div>
        <TldPriceChips fleetId={fleetId} />
      </div>

      {results ? (
        <DomainResultsList
          results={results}
          parsed={parsed}
          chosen={chosen}
          onChoose={setChosen}
          compIncluded={compIncluded}
        />
      ) : null}

      {chosen ? (
        <div className="space-y-3 border-t border-breaker-line pt-3">
          <p className="text-sm text-breaker-mute italic">
            Domains are registered to a real person or business — this registers{' '}
            <span className="not-italic font-medium">you</span> as the owner.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="First name" htmlFor="wr1">
              <Input id="wr1" value={reg.firstName} onChange={(e) => setReg({ ...reg, firstName: e.target.value })} />
            </Field>
            <Field label="Last name" htmlFor="wr2">
              <Input id="wr2" value={reg.lastName} onChange={(e) => setReg({ ...reg, lastName: e.target.value })} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Business name (optional)" htmlFor="wr3">
                <Input id="wr3" value={organization} onChange={(e) => setOrganization(e.target.value)} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Email" htmlFor="wr4">
                <Input id="wr4" type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Address" htmlFor="wr5">
                <Input id="wr5" value={reg.address1} onChange={(e) => setReg({ ...reg, address1: e.target.value })} />
              </Field>
            </div>
            <Field label="City" htmlFor="wr6">
              <Input id="wr6" value={reg.city} onChange={(e) => setReg({ ...reg, city: e.target.value })} />
            </Field>
            <Field label="Postcode" htmlFor="wr7">
              <Input id="wr7" value={reg.postalCode} onChange={(e) => setReg({ ...reg, postalCode: e.target.value })} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Phone" htmlFor="wr8">
                <Input id="wr8" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} placeholder="07… or +44 both fine" />
              </Field>
            </div>
          </div>

          {/* Required T&Cs + registrant consent — the only thing gating Pay. */}
          <RegistrationTermsConsent
            domain={chosen.domain}
            checked={registrationConsent}
            onChange={setRegistrationConsent}
          />

          {/* Optional WHOIS-privacy preference, TLD-honest: .uk has no
              registrar privacy proxy (Nominet lists owners), so we don't offer
              or promise it there. */}
          <WhoisPrivacyControl
            domain={chosen.domain}
            value={whoisPrivacy}
            onChange={setWhoisPrivacy}
          />

          <Button onClick={pay} loading={createOrder.isPending} disabled={!formReady} className="w-full" size="lg">
            {compIncluded
              ? `Get ${chosen.domain} — included`
              : `Pay ${chosen.retailPence !== null ? formatPrice(chosen.retailPence) : ''} — get ${chosen.domain}`}
          </Button>
        </div>
      ) : null}

      {FreeFallbackLink}
    </div>
  );
}

// ---- ③ BYO sub-step ---------------------------------------------------------

export function WizardDomainByoStep({
  fleetId,
  onDomainDone,
}: {
  fleetId: string;
  onDomainDone: () => void;
}) {
  const toast = useToast();
  const view = usePortalDomains(fleetId);
  const claim = useClaimPortalDomain(fleetId);
  const check = useCheckPortalDomain(fleetId);
  const [hostname, setHostname] = useState('');

  const custom = view.data?.custom ?? null;

  if (view.isLoading) {
    return (
      <div className="py-8 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (!custom) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-breaker-mute">
          Enter the address your customers will use — usually a subdomain of your website, like{' '}
          <span className="ref">portal.yourdomain.co.uk</span>.
        </p>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Field label="Your domain" htmlFor="wizByo">
              <Input
                id="wizByo"
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                placeholder="portal.yourdomain.co.uk"
              />
            </Field>
          </div>
          <Button
            size="sm"
            action={() =>
              claim
                .mutateAsync({ hostname: hostname.trim().toLowerCase() })
                .then(() => {
                  recordSetupEvent(fleetId, 'wizard_domain_byo_chosen');
                  toast.success('Domain added — publish the two records when you can');
                })
                .catch((e) => toast.error(e instanceof Error ? e.message : 'Could not add domain'))
            }
            loading={claim.isPending}
            disabled={hostname.trim().length < 4}
          >
            Add
          </Button>
        </div>
      </div>
    );
  }

  const exp = custom.expected;
  return (
    <div className="space-y-4">
      <p className="text-sm text-breaker-mute">
        Add these two records at your DNS host. No rush — we verify in the background and your free
        subdomain works until then, so you can finish setup now.
      </p>
      <div className="rounded-md border border-breaker-line divide-y divide-breaker-line text-sm">
        <div className="p-2">
          <div className="text-2xs uppercase tracking-wide text-breaker-mute">TXT · {exp.txtNameRelative}</div>
          <div className="ref break-all">{exp.txtValue}</div>
        </div>
        <div className="p-2">
          <div className="text-2xs uppercase tracking-wide text-breaker-mute">CNAME · {exp.cnameNameRelative}</div>
          <div className="ref break-all">{exp.cnameTarget ?? 'set a portal subdomain first'}</div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          size="sm"
          action={() =>
            check
              .mutateAsync(undefined)
              .then((r) =>
                r.domain.status === 'pending'
                  ? toast.info('Not visible yet — that&rsquo;s fine, we keep checking')
                  : toast.success('Records found — verifying'),
              )
              .catch(() => undefined)
          }
        >
          Check now (optional)
        </Button>
        <Button className="w-full" size="lg" onClick={onDomainDone}>
          Continue setup — verify later
        </Button>
      </div>
    </div>
  );
}
